/**
 * ArsredovisningPage - Main pillar page for Årsredovisning
 *
 * SEO Target: /arsredovisning
 * Primary keywords: årsredovisning online, digital årsredovisning, skapa årsredovisning
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  PageMeta,
  getBreadcrumbSchema,
  getProductSchema,
  SEO_CONFIG,
} from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { AuthModal } from "@/components/auth/AuthModal";
import { usePricing } from "@/hooks/use-pricing";

// Icons
const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export function ArsredovisningPage() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const pricing = usePricing();

  const breadcrumbItems = [{ label: "Årsredovisning" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
  ]);

  const productSchema = getProductSchema(pricing.priceExclVat);

  return (
    <>
      <PageMeta
        title="Digital Årsredovisning Online | K2 & K3 | Kvanten"
        description={`Skapa din årsredovisning digitalt på 30 minuter. Ladda upp SIE-fil, vi genererar automatiskt. K2 & K3-stöd, direkt inlämning till Bolagsverket. Från ${pricing.formattedPriceExclVat}.`}
        keywords="årsredovisning online, digital årsredovisning, skapa årsredovisning, K2 årsredovisning, K3 årsredovisning, SIE-fil, bokslut online, Bolagsverket"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Digital Årsredovisning Online
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Skapa din K2- eller K3-årsredovisning på under 30 minuter. Ladda upp din SIE-fil
                från bokföringsprogrammet, så gör vi resten. Automatisk validering,
                PDF i Bolagsverkets format och direkt digital inlämning.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Kom igång gratis
                  <ArrowRightIcon />
                </button>
                <Link
                  to="/wizard"
                  className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Prova utan konto
                </Link>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckIcon />
                  <span>Gratis att testa</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckIcon />
                  <span>Betala endast vid export</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckIcon />
                  <span>K2 & K3-stöd</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-blue-600">{pricing.formattedPriceExclVat}</div>
                <p className="text-gray-500 mt-2">exkl. moms per företag/år</p>
              </div>
              <ul className="space-y-4">
                {[
                  "Automatisk SIE-filanalys",
                  "K2 eller K3-årsredovisning",
                  "PDF enligt Bolagsverkets format",
                  "XML för digital inlämning",
                  "Alla obligatoriska noter",
                  "7 års arkivering",
                  "Obegränsad support",
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <p className="text-center text-sm text-gray-500 mt-6">
                Jämfört med byrå: 8 000-25 000 kr
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Så fungerar det</h2>
            <p className="text-xl text-gray-600">Tre enkla steg till färdig årsredovisning</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Ladda upp SIE-fil",
                description:
                  "Exportera SIE4-fil från ditt bokföringsprogram (Fortnox, Visma, Spiris m.fl.) och ladda upp den till Kvanten.",
              },
              {
                step: "2",
                title: "Komplettera uppgifter",
                description:
                  "Vår intelligenta wizard guidar dig genom de uppgifter som inte kan utläsas från SIE-filen, som styrelseledamöter och verksamhetsbeskrivning.",
              },
              {
                step: "3",
                title: "Exportera & Lämna in",
                description:
                  "Granska din färdiga årsredovisning, exportera PDF och XML, och lämna in digitalt till Bolagsverket.",
              },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 text-2xl font-bold rounded-full flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* K2 vs K3 */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">K2 eller K3?</h2>
            <p className="text-xl text-gray-600">
              Vi stödjer båda regelverken - välj det som passar ditt företag
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">K2 Årsredovisning</h3>
              <p className="text-gray-600 mb-6">
                Förenklat regelverk för mindre företag. Passar de flesta aktiebolag i Sverige.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "För mindre aktiebolag",
                  "Förenklad redovisning",
                  "Färre noter krävs",
                  "Schablonmässig värdering",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/arsredovisning/k2"
                className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
              >
                Läs mer om K2
                <ArrowRightIcon />
              </Link>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">K3 Årsredovisning</h3>
              <p className="text-gray-600 mb-6">
                Huvudregelverket för större företag eller de som vill ha mer detaljerad redovisning.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "För större eller frivilligt val",
                  "Kassaflödesanalys ingår",
                  "Uppskjuten skatt redovisas",
                  "Komponentavskrivning",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/arsredovisning/k3"
                className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
              >
                Läs mer om K3
                <ArrowRightIcon />
              </Link>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/arsredovisning/guider/k2-vs-k3"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Osäker? Läs vår guide: K2 vs K3 - Vilket passar ditt företag?
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Varför välja Kvanten för din årsredovisning?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Spara 80% jämfört med byrå",
                description:
                  `En traditionell redovisningsbyrå tar 8 000-25 000 kr för en årsredovisning. Med Kvanten kostar det ${pricing.formattedPriceExclVat}.`,
              },
              {
                title: "Klart på 30 minuter",
                description:
                  "Istället för dagar eller veckor av väntan på konsulten, har du din årsredovisning klar på under en halvtimme.",
              },
              {
                title: "Automatisk K2/K3-validering",
                description:
                  "Vår regelmotor validerar automatiskt mot BFNAR 2016:10 (K2) och BFNAR 2012:1 (K3). Inga regelfel slinker igenom.",
              },
              {
                title: "Ingen revisor behövs",
                description:
                  "De flesta småföretag har inte revisionsplikt. Vi hjälper dig göra hela bokslutet själv, regelrätt.",
              },
              {
                title: "Direkt digital inlämning",
                description:
                  "Exportera XML och lämna in direkt till Bolagsverket. Ingen pappershantering krävs.",
              },
              {
                title: "Svenska servrar & support",
                description:
                  "All data lagras säkert i Sverige. Svensk support som förstår K2/K3-regelverket.",
              },
            ].map((benefit, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guides CTA */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Guider & artiklar</h2>
            <p className="text-xl text-gray-600">
              Lär dig mer om årsredovisning med våra kostnadsfria guider
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Första årsredovisningen",
                description: "Steg-för-steg guide för dig som gör bokslut första gången",
                href: "/arsredovisning/guider/forsta-arsredovisningen",
              },
              {
                title: "SIE-fil guide",
                description: "Allt du behöver veta om SIE-filer och hur du exporterar dem",
                href: "/arsredovisning/guider/sie-fil",
              },
              {
                title: "K2 vs K3",
                description: "Jämförelse och guide för att välja rätt regelverk",
                href: "/arsredovisning/guider/k2-vs-k3",
              },
              {
                title: "Nya regler 2026",
                description: "Kommande förändringar i K2 som påverkar ditt företag",
                href: "/arsredovisning/guider/nya-regler-2026",
              },
            ].map((guide, i) => (
              <Link
                key={i}
                to={guide.href}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{guide.title}</h3>
                <p className="text-gray-600 text-sm">{guide.description}</p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/arsredovisning/guider"
              className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
            >
              Se alla guider
              <ArrowRightIcon />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Redo att skapa din årsredovisning?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Kom igång gratis. Du betalar först när du exporterar din färdiga årsredovisning.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/wizard"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-blue-600 bg-white rounded-lg hover:bg-blue-50 transition-colors"
            >
              Prova direkt
              <ArrowRightIcon />
            </Link>
            <button
              onClick={() => setShowAuthModal(true)}
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white border-2 border-white/30 rounded-lg hover:bg-white/10 transition-colors"
            >
              Skapa konto
            </button>
          </div>
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="register"
      />
    </>
  );
}
